package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.StoricoEntry
import com.heelion.farmafreelance.utils.StimaFiscale
import com.heelion.farmafreelance.utils.TimeUtils
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlin.math.round

/** Un turno che produce guadagno: [maturato] = già svolto, altrimenti previsto (confermato, futuro). */
data class VoceGuadagno(
    val annuncioId: String,
    val giorno: LocalDate,
    val ore: Double,
    /** Tariffa oraria lorda (il compenso dei turni è sempre orario). */
    val tariffa: Double,
    val controparte: String?,
    val maturato: Boolean
) {
    val lordo: Double get() = ore * tariffa
}

/**
 * Totali di un mese: lordo, ENPAF stimato, netto dopo ENPAF e, solo col regime forfettario,
 * l'imposta sostitutiva stimata ([StimaFiscale]); con l'ordinario [imposta] è null.
 */
data class RiepilogoGuadagni(
    val anno: Int,
    /** 1..12; null = riepilogo di più mesi (es. l'anno). */
    val mese: Int?,
    val turni: Int,
    val ore: Double,
    val lordo: Double,
    val lordoPrevisto: Double,
    val enpaf: Double,
    val imposta: Double? = null,
    /** Aliquota usata per [imposta] (es. 15.0). */
    val aliquota: Double? = null
) {
    val netto: Double get() = lordo - enpaf
    /** Netto dopo ENPAF e imposta stimata; null se l'imposta non si stima. */
    val nettoDopoImposte: Double? get() = imposta?.let { netto - it }
}

/**
 * Guadagni del farmacista: lordo = ore x tariffa oraria, con le ore calcolate da
 * [TimeUtils.calculateHours] come nelle fatture (anche turni oltre la mezzanotte), così i numeri
 * coincidono. Turni svolti dallo storico; turni confermati non ancora svolti come "previsti".
 */
object CalcolatoreGuadagni {

    fun voci(
        storico: List<StoricoEntry>,
        matches: List<Match>,
        dettagli: Map<String, AnnuncioTurno>,
        oggi: LocalDate
    ): List<VoceGuadagno> {
        val svolti = storico.map {
            VoceGuadagno(
                it.annuncioId, giorno(it.dataTurno), TimeUtils.calculateHours(it.oraInizio, it.oraFine),
                it.compenso, it.nomeFarmacia, maturato = true
            )
        }
        val giaNelloStorico = svolti.map { it.annuncioId }.toSet()
        val confermati = matches
            .filter { it.stato == MatchStato.CONFERMATO && it.annuncioId !in giaNelloStorico }
            .mapNotNull { m ->
                val a = dettagli[m.annuncioId] ?: return@mapNotNull null
                val g = giorno(a.dataTurno)
                VoceGuadagno(
                    m.annuncioId, g, TimeUtils.calculateHours(a.oraInizio, a.oraFine),
                    a.compenso ?: 0.0, m.nomeControparte, maturato = g < oggi
                )
            }
        return (svolti + confermati).sortedBy { it.giorno }
    }

    /**
     * Totali delle voci che cadono in [periodo]; [percEnpaf] è la percentuale del profilo (es. 2.0),
     * [aliquota] quella del forfettario (es. 15.0) o null se l'imposta non si stima.
     */
    fun riepilogo(voci: List<VoceGuadagno>, periodo: PeriodoGuadagni, percEnpaf: Double, aliquota: Double? = null): RiepilogoGuadagni {
        val dentro = voci.filter { periodo.contiene(it.giorno) }
        val lordo = dentro.sumOf { it.lordo }
        val enpaf = arrotonda(lordo * percEnpaf / 100.0)
        return RiepilogoGuadagni(
            anno = periodo.anno, mese = periodo.mese,
            turni = dentro.size,
            ore = dentro.sumOf { it.ore },
            lordo = arrotonda(lordo),
            lordoPrevisto = arrotonda(dentro.filter { !it.maturato }.sumOf { it.lordo }),
            enpaf = enpaf,
            imposta = aliquota?.let { StimaFiscale.imposta(lordo, enpaf, it) },
            aliquota = aliquota
        )
    }

    /** Un riepilogo per ogni mese con turni, dal più recente. */
    fun perMese(voci: List<VoceGuadagno>, percEnpaf: Double, aliquota: Double? = null): List<RiepilogoGuadagni> =
        voci.map { it.giorno.year to it.giorno.monthNumber }.distinct()
            .sortedWith(compareByDescending<Pair<Int, Int>> { it.first }.thenByDescending { it.second })
            .map { (anno, mese) -> riepilogo(voci, PeriodoGuadagni(anno, mese), percEnpaf, aliquota) }

    // Come TimeUtils.combineDateAndTime: il giorno del turno è la data UTC di dataTurno.
    private fun giorno(dataTurno: Instant) = dataTurno.toLocalDateTime(TimeZone.UTC).date

    private fun arrotonda(v: Double) = round(v * 100) / 100
}

/** Un mese ([mese] 1..12) o un anno intero ([mese] null). */
data class PeriodoGuadagni(val anno: Int, val mese: Int?) {
    fun contiene(g: LocalDate) = g.year == anno && (mese == null || g.monthNumber == mese)

    companion object {
        private val MESI = listOf("gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
            "agosto", "settembre", "ottobre", "novembre", "dicembre")

        /** "a ottobre", "il mese scorso", "quest'anno", "nel 2025"; di default il mese corrente. */
        fun estrai(testo: String, oggi: LocalDate): PeriodoGuadagni {
            val t = IntentClassifier.normalizza(testo)
            val anno = Regex("\\b(20\\d{2})\\b").find(t)?.groupValues?.get(1)?.toInt()
            MESI.indexOfFirst { t.contains(it) }.takeIf { it >= 0 }?.let { i ->
                // Un mese nominato senza anno: quello più recente non futuro (a gennaio, "dicembre" = l'anno scorso).
                val a = anno ?: if (i + 1 > oggi.monthNumber) oggi.year - 1 else oggi.year
                return PeriodoGuadagni(a, i + 1)
            }
            if (t.contains("mese scorso") || t.contains("scorso mese")) {
                val m = oggi.minus(1, DateTimeUnit.MONTH)
                return PeriodoGuadagni(m.year, m.monthNumber)
            }
            if (anno != null || t.contains("quest anno") || t.contains("anno") || t.contains("da gennaio") || t.contains("finora")) {
                return PeriodoGuadagni(anno ?: oggi.year, null)
            }
            return PeriodoGuadagni(oggi.year, oggi.monthNumber)
        }
    }

    fun descrizione(): String = if (mese == null) "Nel $anno" else "A ${MESI[mese - 1]} $anno"
}
